import React, { useState, useRef, useEffect } from 'react';
import { Send, MessageSquare, Sparkles } from 'lucide-react';

interface ChatInterfaceProps {
    onGenerateReport: (query: string) => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ onGenerateReport }) => {
    const [query, setQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const exampleQueries = [
        "生成2025年7月的公司财务分析报告"
    ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!query.trim() || isLoading) return;

        setIsLoading(true);
        try {
            await onGenerateReport(query.trim());
        } finally {
            setIsLoading(false);
            setQuery('');
        }
    };

    const handleExampleClick = (exampleQuery: string) => {
        setQuery(exampleQuery);
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    // 自动调整textarea高度
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
    }, [query]);

    return (
        <div className="max-w-4xl mx-auto">
            {/* 欢迎区域 */}
            <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                    <div className="p-4 bg-primary-100 rounded-full">
                        <Sparkles className="h-12 w-12 text-primary-600" />
                    </div>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    财务分析报告生成Agent
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                    基于AI的财务分析报告自动生成平台
                </p>
                <p className="text-sm text-gray-500">
                    支持报告类型：公司月度财务分析报告
                </p>
            </div>

            {/* 输入区域 */}
            <div className="card mb-8">
                <form onSubmit={handleSubmit}>
                    <div className="relative">
                        <textarea
                            ref={textareaRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="请输入您的分析需求，例如：生成2025年7月的公司财务分析报告"
                            className="input-field resize-none min-h-[120px] pr-12"
                            disabled={isLoading}
                        />
                        <button
                            type="submit"
                            disabled={!query.trim() || isLoading}
                            className="absolute bottom-3 right-3 p-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
                        >
                            {isLoading ? (
                                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                            ) : (
                                <Send className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* 示例查询 */}
            <div className="card">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <MessageSquare className="h-5 w-5 mr-2 text-primary-600" />
                    示例查询
                </h3>
                <div className="grid gap-3 md:grid-cols-1">
                    {exampleQueries.map((example, index) => (
                        <button
                            key={index}
                            onClick={() => handleExampleClick(example)}
                            className="p-4 text-left border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200 group"
                            disabled={isLoading}
                        >
                            <div className="flex items-start">
                                <div className="flex-shrink-0 w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 group-hover:bg-primary-600 transition-colors" />
                                <div className="flex-1">
                                    <p className="text-gray-700 group-hover:text-gray-900">
                                        {example}
                                    </p>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
                <div className="mt-4 text-sm text-gray-500">
                    💡 点击示例文本快速填充，或直接输入您自己的查询需求
                </div>
            </div>

            {/* 功能特性 */}
            <div className="mt-8 grid gap-6 md:grid-cols-3">
                <div className="text-center">
                    <div className="p-3 bg-success-100 rounded-full w-fit mx-auto mb-3">
                        <Sparkles className="h-6 w-6 text-success-600" />
                    </div>
                    <h4 className="font-semibold mb-1">Agent驱动</h4>
                    <p className="text-sm text-gray-600">AI驱动的财务分析报告生成</p>
                </div>
                <div className="text-center">
                    <div className="p-3 bg-primary-100 rounded-full w-fit mx-auto mb-3">
                        <Send className="h-6 w-6 text-primary-600" />
                    </div>
                    <h4 className="font-semibold mb-1">实时生成</h4>
                    <p className="text-sm text-gray-600">流式展示报告生成进度和内容</p>
                </div>
                <div className="text-center">
                    <div className="p-3 bg-warning-100 rounded-full w-fit mx-auto mb-3">
                        <MessageSquare className="h-6 w-6 text-warning-600" />
                    </div>
                    <h4 className="font-semibold mb-1">专业报告</h4>
                    <p className="text-sm text-gray-600">生成符合行业标准的财务分析报告</p>
                </div>
            </div>
        </div>
    );
};

export default ChatInterface;
